"use client";

import Link from "next/link";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import type { StoryChapter } from "@/lib/stories";
import { cn } from "@/lib/utils";

import StoryVideo from "./story-video";
import { useIdleChrome } from "./use-idle-chrome";
import { useVideoTransport } from "./use-video-transport";
import VideoTransportControls from "./video-transport";

export interface LightboxStory {
  slug: string;
  title: string;
  name: string;
  role: string;
  location: string;
  poster: string;
  posterAlt: string;
  /** Embed URL from `youtubeEmbedSrc`. */
  src: string;
  videoDuration?: string;
  chapters?: StoryChapter[];
}

interface StoryLightboxProps {
  story: LightboxStory;
  /** Title of the next story in the deck, shown as an up-next hint. */
  upNextTitle?: string;
  onClose: () => void;
}

const FOCUSABLE = "a[href], button:not([disabled]), input:not([disabled])";

const SEEK_STEP_SECONDS = 5;

/**
 * The "watching in focus" state: the page behind is blurred rather than merely
 * dimmed, so a light index defocuses instead of turning into unreadable grey
 * text under a scrim.
 *
 * Controls are drawn rather than native because the design puts them inside the
 * frame alongside the speaker and chapter overlays; they come from
 * `VideoTransportControls`, shared with the player on each story page.
 */
export default function StoryLightbox({
  story,
  upNextTitle,
  onClose,
}: StoryLightboxProps) {
  const [mounted, setMounted] = useState(false);
  // Separate from `mounted` on purpose: the entrance classes have to be applied
  // on a later frame than the first paint, or the browser has nothing to
  // transition from and the player simply appears.
  const [entered, setEntered] = useState(false);

  const transport = useVideoTransport();

  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);

  const chrome = useIdleChrome(frameRef, transport.playing);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      setEntered(true);
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [mounted]);

  // Focus moves in only once the portal is on the page: the first render
  // returns null, so a ref read before `mounted` flips finds nothing to focus
  // and the dialog is left with focus still on the card behind it, where Tab
  // walks straight out of an `aria-modal`. Restores to the opener on close,
  // rather than dropping focus at the top of the document.
  useEffect(() => {
    if (!mounted) {
      return;
    }
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => {
      opener?.focus?.();
    };
  }, [mounted]);

  useEffect(() => {
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
    };
  }, []);

  const { togglePlay, seekBy } = transport;

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      // Let the range input keep its own arrow handling.
      const onSlider =
        document.activeElement instanceof HTMLInputElement &&
        document.activeElement.type === "range";

      if (!onSlider && (event.key === "k" || event.key === " ")) {
        event.preventDefault();
        togglePlay();
        return;
      }
      if (!onSlider && event.key === "ArrowLeft") {
        event.preventDefault();
        seekBy(-SEEK_STEP_SECONDS);
        return;
      }
      if (!onSlider && event.key === "ArrowRight") {
        event.preventDefault();
        seekBy(SEEK_STEP_SECONDS);
        return;
      }
      if (event.key !== "Tab") {
        return;
      }
      const focusable =
        dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!focusable || focusable.length === 0) {
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose, seekBy, togglePlay],
  );

  useEffect(() => {
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onKeyDown]);

  // Memoised so the controls are not handed a fresh empty-array literal, and a
  // fresh chapter lookup, on every render.
  const chapters = useMemo(() => story.chapters ?? [], [story.chapters]);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void frameRef.current?.requestFullscreen?.();
    }
  }, []);

  if (!mounted) {
    return null;
  }

  // Element by element rather than one wrapper: a full-frame overlay would
  // swallow the click that pauses the clip.
  const chromeClass = cn(
    "transition-opacity duration-500",
    chrome.visible ? "opacity-100" : "opacity-0",
  );

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={story.title}
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center px-4 py-10 sm:px-10 lg:py-14"
    >
      {/* The blur is applied up front rather than animated: transitioning a
          30px backdrop filter costs a full-frame re-blur and reads no better
          than fading the finished result in. */}
      <div
        aria-hidden
        onClick={onClose}
        className={cn(
          "absolute inset-0 bg-black/70 backdrop-blur-[30px] transition-opacity duration-300 ease-out",
          entered ? "opacity-100" : "opacity-0",
        )}
      />

      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        aria-label="Close video"
        className="absolute top-6 right-6 flex size-11 items-center justify-center rounded-full border border-white/30 bg-white/15 text-white transition-colors hover:bg-white/25 sm:top-10 sm:right-10"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden fill="none">
          <path
            d="M2.5 2.5l11 11M13.5 2.5l-11 11"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {/*
        Capped by height as well as width. 1180 alone made the dialog taller than
        a laptop screen: at 1366x768 the frame started 10px above the top edge and
        the buttons finished below the bottom one, and at 1280x800 there were 6px
        of clearance at each end.

        14.75rem is what the frame shares the screen with, measured rather than
        estimated: 124px of title and buttons below it (a 92px block plus its own
        32px top margin, which is the part that is easy to miss and was worth 16px
        off each gap), plus the 56px top and bottom that `lg:py-14` reserves. The
        20rem floor keeps a very short window from shrinking the video to a stamp.
      */}
      <div
        style={{
          maxWidth: "min(1180px, max(20rem, (100svh - 14.75rem) * 1.7778))",
        }}
        className={cn(
          "relative w-full transition-[transform,opacity] duration-[400ms] ease-out",
          entered ? "scale-100 opacity-100" : "scale-[0.96] opacity-0",
        )}
      >
        <div
          ref={frameRef}
          className="relative aspect-video overflow-hidden rounded-2xl bg-black shadow-[0_28px_80px_-12px_rgba(0,0,0,0.55)]"
        >
          <StoryVideo
            src={story.src}
            poster={story.poster}
            posterSizes="(max-width: 1180px) 100vw, 1180px"
            title={story.title}
            autoPlay
            started={transport.started}
            attach={transport.attach}
            onPress={() => {
              // A tap that brought the chrome back is not also asking to pause.
              // With a pointer the chrome is up long before the click, so this
              // only changes what the first touch does.
              if (!chrome.pressRevealed()) {
                togglePlay();
              }
            }}
          />

          <div
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/60 to-transparent",
              chromeClass,
            )}
          />
          <div
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/75 to-transparent",
              chromeClass,
            )}
          />

          {/* Same insets as the story page's clip panel, which grow with the
              frame so the name is not pinned to the corner at full width. */}
          <div
            className={cn(
              "pointer-events-none absolute top-5 left-6 sm:top-6 sm:left-8 lg:top-8 lg:left-10",
              chromeClass,
            )}
          >
            <p className="text-[1.375rem] font-medium tracking-tight text-white">
              {story.name}
            </p>
            <p className="mt-1.5 font-mono text-[0.625rem] tracking-[0.1em] text-white/60 uppercase">
              {story.role} · {story.location}
            </p>
          </div>

          <VideoTransportControls
            transport={transport}
            chapters={chapters}
            runtime={story.videoDuration}
            onToggleFullscreen={toggleFullscreen}
            retracted={!chrome.visible}
            className={chromeClass}
          />
        </div>

        <div className="mt-8 flex flex-col items-center gap-4">
          <p className="text-center text-2xl font-medium tracking-tight text-white/95">
            {story.title}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            <Link
              href={`/stories/${story.slug}`}
              className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-medium text-neutral-900 transition-colors hover:bg-white/90"
            >
              Read the full story
              <svg
                width="15"
                height="15"
                viewBox="0 0 15 15"
                aria-hidden
                fill="none"
              >
                <path
                  d="M3 7.5h8M7.5 4l3.5 3.5-3.5 3.5"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
            {upNextTitle && (
              <span className="inline-flex items-center gap-2 font-mono text-[0.625rem] tracking-[0.1em] text-white/50 uppercase">
                <span className="size-1.5 rounded-full bg-white/45" />
                Up next · {upNextTitle}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
